/**
 * Types of all the widget models for request data
 */
export const widgetType = {
  GEO: "geo",
  GEO_STATIC: "geo_static",
  TIME: "time",
  SINGLE: "single",
  CATEGORICAL: "categorical",
  DETAILS: "details",
  DISTRIBUTION: "distribution",
};

/**
 * Class representing the aggregations on each entity property
 */
export class MetricAggregation {
  /**
   * Takes in an aggregation function and the property
   * @param {String} func
   * @param {String} prop
   */
  constructor(func, prop) {
    this.func = func;
    this.prop = prop;
  }

  toJSON() {
    return {
      func: this.func,
      prop: this.prop,
    };
  }
}

/**
 * Combination of Metric aggregation and the entity filters applied on it
 * This defines the single unit of each metric
 */
export class MetricTerm {
  /**
   * @param {String} name
   * @param {String} entity_id
   * @param {Array} entity_filters
   * @param {MetricAggregation} agg
   */
  constructor(name, entity_id, entity_filters, entity_type, agg) {
    this.name = name;
    this.entity_id = entity_id;
    this.entity_filters = entity_filters;
    this.entity_type = entity_type;
    this.agg = agg;
  }

  toJSON() {
    if (this.entity_filters) {
      return {
        name: this.name,
        entity_id: this.entity_id,
        entity_type: this.entity_type,
        entity_filters: this.entity_filters,
        agg: this.agg.toJSON(),
      };
    } else {
      return {
        name: this.name,
        entity_id: this.entity_id,
        entity_type: this.entity_type,
        agg: this.agg.toJSON(),
      };
    }
  }
}

/**
 * Defines each metric as a mathematical expression
 * A collection of terms and expression
 */
export class WidgetMetric {
  /**
   *
   * @param {Array[String]} terms
   * @param {String} expression
   */
  constructor(terms, expression) {
    this.terms = terms;
    this.expression = expression;
  }

  toJSON() {
    return {
      expr_terms: this.terms.map(term => term.toJSON()),
      expr_str: this.expression,
    };
  }
}

/**
 * Defines the data contained in each Widget
 */
export class Widget {
  /**
   *
   * @param {String} type
   * @param {Object} meta
   * @param {WidgetMetric} metric
   */
  constructor(type, meta, metric) {
    this.type = type;
    this.meta = meta;
    this.metric = metric;
  }

  toJSON() {
    return {
      widget_type: this.type,
      meta: this.meta,
      metric: this.metric.toJSON(),
    };
  }
}

/**
 * GEO widget for data related to map
 */
export class GeoWidget extends Widget {
  /**
   *
   * @param {Object} meta
   * @param {WidgetMetric} metric
   */
  constructor(meta, metric) {
    super(widgetType.GEO, meta, metric);
  }

  toJSON() {
    return super.toJSON();
  }
}

/**
 * Widget for time series data
 */
export class TimeWidget extends Widget {
  /**
   *
   * @param {Object} meta
   * @param {WidgetMetric} metric
   */
  constructor(meta, metric) {
    super(widgetType.TIME, meta, metric);
  }

  toJSON() {
    return super.toJSON();
  }
}

/**
 * Widget related to SingleValues in the layers
 */
export class SingleValueWidget extends Widget {
  /**
   *
   * @param {Object} meta
   * @param {WidgetMetric} metric
   */
  constructor(meta, metric) {
    super(widgetType.SINGLE, meta, metric);
  }

  toJSON() {
    return super.toJSON();
  }
}

/**
 * Categorical widgets
 */
export class CategoricalWidget extends Widget {
  /**
   *
   * @param {Object} meta
   * @param {WidgetMetric} metric
   * @param { Array[String]} cols Column names of the widgets
   */
  constructor(meta, metric, col) {
    super(widgetType.CATEGORICAL, meta, metric);
    this.col = col;
  }

  /**
   * Overrride toJSON of parent to incorporate columns
   */
  toJSON() {
    return {
      ...super.toJSON(),
      col: this.col ? this.col : [],
    };
  }

  /**
   * Used to set columns for filter widget
   * @param {*} columns
   */
  setColumns(columns) {
    this.col = columns;
  }
}
